// src/pages/MarketMap/HistoricalChartPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { normalizeValue } from "lib/marketFormat";
import ChartTooltip from "components/ChartTooltip";
import ModalBackdrop from "components/ModalBackdrop";

/** Format Date.now() - N days into YYYY-MM-DD string */
function cutoffString(days) {
  const d = new Date(Date.now() - days * 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

// Format YYYY-MM-DD string as m/d/yyyy (for axis ticks only)
function formatDateLabel(str) {
  if (!str) return "";
  const d = new Date(String(str).slice(0, 10) + "T00:00:00"); // force local midnight
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

// Format numbers with 2 decimals and proper units
function formatValue(val, units) {
  if (val == null || Number.isNaN(val)) return "";
  const num = Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!units) return num;
  const u = String(units).trim();
  if (!u) return num;
  if (u === "%" || u.toLowerCase().includes("bps")) return `${num}${u}`;
  if (u.startsWith("$")) return `${u}${num}`;
  return `${num} ${u}`;
}

export default function HistoricalChartPanel({
  open,
  symbol,
  displayName,
  loading,
  data = [],
  onClose,
  units: unitsProp,
}) {
  const [timeframe, setTimeframe] = useState("90");

  useEffect(() => {
    if (!open) setTimeframe("90");
  }, [open]);

  const seriesDisplayName = useMemo(() => {
    if (displayName) return displayName;
    if (Array.isArray(data) && data.length) {
      return data[0]?.raw?.name || data[0]?.raw?.ticker || symbol || "Series";
    }
    return symbol || "Series";
  }, [data, displayName, symbol]);

  const unitsFmt =
    (typeof unitsProp !== "undefined" && unitsProp !== null ? unitsProp : null) ??
    (Array.isArray(data) && data.length ? (data[0]?.raw?.units ?? data[0]?.units ?? "") : "") ??
    "";

  const filtered = useMemo(() => {
    if (!Array.isArray(data) || !data.length) return [];

    const normalized = data.map((d) => ({
      ...d,
      value: normalizeValue(symbol, d.value),
      date: d.date ? String(d.date).slice(0, 10) : null,
      label: d.label ? String(d.label).slice(0, 10) : null,
    }));

    if (timeframe === "all") return normalized;

    const days = Number(timeframe);
    if (!days || days <= 0) return normalized;

    const cutoff = cutoffString(days);
    return normalized.filter((p) => p.date && p.date >= cutoff);
  }, [data, timeframe, symbol]);

  const { ticks, yDomain } = useMemo(() => {
    const vals = filtered
      .map((d) => (typeof d.value === "number" ? d.value : NaN))
      .filter(Number.isFinite);

    if (!vals.length) {
      return { ticks: null, yDomain: ["auto", "auto"] };
    }

    const minVal = Math.min(...vals);
    const maxVal = Math.max(...vals);

    const anchorSteps = [0.25, 0.5, 1];
    const tickCandidates = [1, 0.5, 0.25, 0.1, 0.05, 0.01];

    let finalMin, finalMax, ticksArray;

    outer: for (const anchor of anchorSteps) {
      const testMin = Math.floor(minVal / anchor) * anchor;
      const testMax = Math.ceil(maxVal / anchor) * anchor;
      const range = +(testMax - testMin).toFixed(8);
      if (range <= 0) continue;

      const ideal = range / 4;
      const step = tickCandidates
        .filter((s) => s <= ideal + 1e-8)
        .sort((a, b) => b - a)[0];
      if (!step) continue;

      finalMin = testMin;
      finalMax = testMax;

      const t = [];
      for (let v = finalMin; v <= finalMax + 1e-8; v += step) t.push(+v.toFixed(8));
      ticksArray = t;
      break outer;
    }

    if (!ticksArray) {
      finalMin = Math.floor(minVal / 0.25) * 0.25;
      finalMax = Math.ceil(maxVal / 0.25) * 0.25;
      const step = (finalMax - finalMin) / 4 || 1;
      const t = [];
      for (let v = finalMin; v <= finalMax + 1e-8; v += step) t.push(+v.toFixed(8));
      ticksArray = t;
    }

    return { ticks: ticksArray, yDomain: [finalMin, finalMax] };
  }, [filtered]);

  const leftMargin = 48;

  if (!open) return null;

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="relative bg-app border rounded-lg w-full max-w-5xl h-[75vh] p-4 overflow-auto shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="market-history-title"
      >
        {/* Top-right chrome close (panel owns X) */}
        <button
          type="button"
          onClick={onClose}
          className={[
            "absolute right-3 top-3 z-10",
            "inline-flex items-center justify-center",
            "h-8 w-8 rounded-md",
            "text-muted hover:text-app",
            "hover:bg-[hsl(var(--muted))]/50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2",
            "focus-visible:ring-offset-[hsl(var(--background))]",
            "transition-colors",
          ].join(" ")}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-between mb-3 pr-12">
          <h3 id="market-history-title" className="text-lg font-semibold truncate">
            {seriesDisplayName}
          </h3>

          <div className="flex items-center gap-2">
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="bg-app text-app border rounded px-2 py-1 text-sm"
            >
              <option value="30">30D</option>
              <option value="90">90D</option>
              <option value="183">6M</option>
              <option value="365">1Y</option>
            </select>
          </div>
        </div>

        <div className="h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center h-full text-muted">
              Loading series…
            </div>
          ) : !data || !data.length ? (
            <div className="text-muted">No history available for this series.</div>
          ) : !filtered || !filtered.length ? (
            <div className="text-muted">No data in the selected timeframe.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={filtered}
                margin={{ top: 8, right: 18, bottom: 24, left: leftMargin }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="label"
                  tickFormatter={formatDateLabel}
                  tickMargin={10}
                  tick={{ fill: "hsl(var(--muted-foreground))" }} // themed axis labels
                />

                <YAxis
                  domain={yDomain}
                  ticks={ticks}
                  tickFormatter={(v) => formatValue(v, unitsFmt)}
                  tick={{ fill: "hsl(var(--muted-foreground))" }} // themed axis labels
                />

                <Tooltip
                  content={
                    <ChartTooltip
                      formatter={(val) => [formatValue(val, unitsFmt), seriesDisplayName]}
                    />
                  }
                />

                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="var(--chart-1)"
                  dot={false}
                  strokeWidth={3}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
